import React, { useEffect, useState } from 'react';
import Module from '../../../../layout/Module';
import PreviewList from './PreviewList';
import ButtonStartIcon from '../../../../button/ButtonStartIcon';
import { useNavigate } from 'react-router-dom';
import AlertSaveButton from '../../../../button/AlertSaveButton';
import {
  insertLogUpload,
  presigneUrl
} from '../../../../../services/photo-management/photo-management.service';
import { uploadFile } from '../../../../../services/upload/upload.service';
import { handlerErrorV2 } from '../../../../../utils/errors';
import { Alert, Typography } from '@mui/material';
import LinearProgress from '../../../../progress/LinearProgress';
import FormUpload from './Form';
import isEmpty from 'lodash/isEmpty';
import IconTooltipButton from '../../../../button/IconTooltipButton';
import { toggleAlertModule } from '../../../../../store/actions/alert.actions';
import { useDispatch, useSelector } from 'react-redux';

const Upload = () => {
  const maxFileSize = 20 * 1024 * 1024; // 20 MB
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUpload, setCurrentUpload] = useState(null);
  const [percent, setPercent] = useState(0);
  const [uploaded, setUploaded] = useState([]);

  const alertSave = useSelector((state) => state?.alert?.save || {});
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const onCancel = () => {
    navigate('/dashboard/photo-management');
  };

  useEffect(() => {
    if (!alertSave.mode || !alertSave.onSave) {
      if (!isEmpty(files)) {
        dispatch(toggleAlertModule(false, 'Photo Management', 'upload', uploadPhoto, alertSave.onDontSave));
      }
    }
  }, [files, alertSave]);

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]);

  const uploadPhoto = async () => {
    if (isEmpty(files)) {
      setError('Please select at least one photo.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setPercent(0);
    setCurrentUpload(null);
    setUploaded([]);
    try {
      const resultUpload = await presigneUrl(files);
      if (resultUpload.error || !resultUpload.data) {
        return { error: resultUpload.error.message };
      } else {
        const result = [];
        for (let indexFile in resultUpload.data) {
          const file = resultUpload.data[indexFile];
          const { data, error } = file;

          if (!data) {
            result.push({
              id: error.file,
              name: error.file,
              link: `photos/${error.file}`,
              error: error?.msg || 'Something went wrong. Please try again.'
            });
            setUploaded((prev) => [...prev, { name: error?.file, error: error?.msg || 'Something went wrong. Please try again.' }]);
          }
          setCurrentUpload(data?.file?.name);
          setPercent(((Number(indexFile) + 1) / resultUpload.data.length) * 100);

          const currentPhoto = files.find((item) => item.name === data?.file?.name);

          if (currentPhoto) {
            let error = null;
            await uploadFile(data.presignedUrl, currentPhoto.file).catch((err) => {
              error = handlerErrorV2(err);
            });
            result.push({
              id: data.file.id,
              name: data.file?.name,
              link: `photos/${data.filename}`,
              error
            });
            setUploaded((prev) => [...prev, { name: data?.file?.name, error }]);
          }
        }

        if (!result) {
          return { error: 'Something went wrong. Please try again.' };
        } else {
          dispatch(toggleAlertModule());

          await insertLogUpload(result.map((item) => ({
            name: item.name,
            msg: item.error ? item.error : 'Photo uploaded',
            error: !!item.error
          })));
          return { error: null, data: result };
        }
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };
  const onReset = () => {
    setFiles([]);
    setPercent(0);
    setUploaded([]);
  };

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary justify-between'>
        <div className='w-full'>
          {isLoading && (
            <>
              <LinearProgress value={percent} />
              {currentUpload && (
                <div className='text-muted-foreground text-sm'>Uploading {currentUpload}</div>
              )}
            </>
          )}
          {!isLoading && percent === 100 && (
            <Typography className='px-2'>
              {uploaded?.filter((el) => !el.error)?.length}/{files?.length} Photos uploaded.
            </Typography>
          )}
          {error && <Alert severity='error'>{error}</Alert>}
        </div>
        <div className='flex gap-2'>
          {percent === 100 && !isLoading && (
            <>
              <IconTooltipButton
                id='reset'
                title='Reset'
                icon='redo'
                onClick={onReset}
                style={{
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--primary)',
                  color: 'var(--primary-foreground)',
                  padding: '0.45rem'
                }}
              />
              <div className='px-1' />
            </>
          )}
          <AlertSaveButton
            submodule='Photo Management'
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm'
            onDontSave={onCancel}
            disabled={isLoading}
          />
          <ButtonStartIcon
            color='primary'
            variant='contained'
            startIcon={['fas', 'upload']}
            loading={false}
            onClick={uploadPhoto}
            disabled={isLoading || percent === 100}
          >
            Upload
          </ButtonStartIcon>
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer>
        <FormUpload
          files={files}
          setFiles={setFiles}
          isLoading={isLoading || percent === 100}
          maxFileSize={maxFileSize}
        />
        <PreviewList
          maxFileSize={maxFileSize}
          files={files}
          setFiles={setFiles}
          isLoading={isLoading || percent === 100}
          uploaded={uploaded}
        />
      </Module.BodyContainer>
    </>
  );
};

export default Upload;
